import { Fragment } from 'react'
import { Helmet } from 'react-helmet-async'
import { Box, Container, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Background from '../resources/images/background.png'

const ViewingContactMarketplaceListingDisclaimer = () => {
  const theme = useTheme()

  return (
    <Fragment>
      <Helmet>
        <title>
          Viewing Contact Marketplace Listing Disclaimer |{' '}
          {process.env.REACT_APP_APP_NAME}
        </title>
      </Helmet>
      <Box
        sx={{
          backgroundImage: `url(${Background})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: 5, color: 'common.white' }}>
            <Typography variant="body1" mt>
              Welcome to Pitchblu (the{' '}
              <span style={{ fontWeight: 700, marginLeft: '4px' }}>
                "Platform"
              </span>
              ). We are pleased to provide you with the ability to view the
              contact details associated with Marketplace Listings (as defined
              herein). However, it is important to understand and adhere to the
              following guidelines before accessing any such contact details to
              ensure a safe and secure environment for all User(s). (
              <span style={{ fontWeight: 700 }}>Users</span> refers to
              individuals who access and utilize the Platform for the purpose of
              engaging in communication, networking, and establishing business
              or professional relationships with other individuals on the
              Platform).
            </Typography>

            <Typography variant="body1" mt>
              Prior to viewing any contact details of a Marketplace Listing,
              please carefully review the following disclaimer:
            </Typography>

            <Typography
              variant="h1"
              color="primary.main"
              textAlign="center"
              sx={{ textDecoration: 'underline', mt: 2 }}
            >
              DISCLAIMER – VIEWING CONTACT FOR MARKETPLACE LISTINGS
            </Typography>

            {/* 1. Definition */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              1. Definition of Marketplace Listings:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              “Marketplace Listings” means any Content submitted, uploaded, or
              shared by Users on the Platform for the purpose of listing,
              advertising, offering, selling, renting, or providing information
              about film-related goods, equipment, props, costumes, tools,
              materials, or services intended for creative, filming,
              photography, or production-related use.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              All Marketplace Listings are entirely User-generated, and Pitchblu
              does not own, manufacture, store, inspect, manage, control,
              verify, or endorse any goods or services listed on the Platform.
            </Typography>

            {/* 2. User Responsibility */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              2. User Responsibility:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              By choosing to view and use contact information provided in a
              Marketplace Listing, you acknowledge and agree that:
            </Typography>
            <Box ml={5} mt>
              <ul>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    You are solely responsible for all communications,
                    negotiations, payments, deliveries, pickups, service
                    engagements, and transactions conducted with the other
                    User(s).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    You must exercise independent due diligence regarding the
                    legality, safety, condition, authenticity, ownership, and
                    suitability of any goods or services offered and the person
                    providing the contact details.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    You shall not disclose or share your own sensitive personal,
                    financial, or proprietary information unless you have taken
                    appropriate precautions and are fully satisfied with the
                    trustworthiness of the recipient.
                  </Typography>
                </li>
              </ul>
            </Box>

            {/* 3. Prohibited Conduct */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              3. Prohibited Conduct:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You acknowledge and agree that you shall not:
            </Typography>
            <Box ml={5} mt>
              <ul>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    Use the contact information for any unlawful, unsafe,
                    fraudulent, or misleading purpose.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    Harass, threaten, or abuse the other User(s).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    Misrepresent your identity, intent, or authority in
                    connection with any Marketplace interaction
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" mt textAlign="justify">
                    Misuse, extract, or share obtained contact details with
                    third parties without the explicit consent of the concerned
                    User(s).
                  </Typography>
                </li>
              </ul>
            </Box>
            <Typography variant="body1" mt textAlign="justify">
              Any breach of this clause may result in suspension, restriction,
              removal of access, or termination of your account, at the sole
              discretion of the Platform, in addition to any legal remedies
              available under applicable law.
            </Typography>

            {/* 4. Limitation of Liability */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              4. Limitation of Liability:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You acknowledge and agree that Pitchblu shall bear no
              responsibility or liability, to the maximum extent permissible by
              law, for any consequences arising from your decision to view, rely
              upon, or use contact information associated with a Marketplace
              Listing.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              This includes, but is not limited to, claims relating to defective
              or unsafe goods, service failures, personal injury, property
              damage, fraud, misrepresentation, delivery issues,
              non-performance, financial losses, harassment, reputational harm,
              or disputes of any nature between Users.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu does not verify, authenticate, or endorse the accuracy,
              completeness, reliability, or legitimacy of any contact details
              displayed. All Marketplace Listings and associated contact
              information are provided strictly on an “as is” basis.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Any communications or transactions undertaken based on contact
              information obtained through the Platform are conducted entirely
              at your own risk and responsibility.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu may, at its sole discretion, review, flag, restrict, or
              remove Marketplace Listings or contact access, but assumes no
              obligation to do so.
            </Typography>

            {/* 5. Platform Rights */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              5. Platform Rights:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              You hereby acknowledge and agree that the Platform reserves the
              right to manage, modify, delete, restrict, or conceal any Content
              uploaded on the Platform.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              itchblu further reserves the right, but not the obligation, to
              restrict, suspend, revoke, or monitor access to contact
              information if it reasonably suspects misuse, unlawful activity,
              policy violations, or conduct that may expose the Platform or its
              Users to risk.
            </Typography>

            {/* 6. Changes to Disclaimer */}
            <Typography
              variant="h4"
              fontWeight={700}
              mt={2}
              color="primary.main"
            >
              6. Changes to Disclaimer:
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              Pitchblu reserves the right to modify or update this disclaimer at
              any time without prior notice. Users are advised to review this
              disclaimer periodically to remain informed of any changes.
            </Typography>
            <Typography variant="body1" mt textAlign="justify">
              By choosing to view contact details associated with a Marketplace
              Listing, you acknowledge that you have read, understood, and
              agreed to abide by the terms of this disclaimer, the Terms of
              Service, and the Privacy Policy, accessible at{' '}
              <Link
                to="/privacy-policy"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/privacy-policy`}
              </Link>{' '}
              and{' '}
              <Link
                to="/terms-and-conditions"
                style={{
                  fontWeight: 600,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                }}
                target="_blank"
              >
                {`${window.location.origin}/terms-and-conditions`}
              </Link>
              . If you do not agree with these terms, you are advised not to
              proceed.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Fragment>
  )
}

export default ViewingContactMarketplaceListingDisclaimer
